/**
 * Settings, the web twin of the native Settings window (macOS) and sheet
 * (iOS), apps/swift Views/Settings/SettingsView.swift. One dialog with a small
 * page stack: root → Account / Theme / Typography / Editor.
 *
 * Desktop: a centred window-sized panel with a "Done" button. Mobile: a
 * full-height sheet with an X, rows grouped iOS-style.
 *
 * It replaces the old sidebar user menu, so every action that menu had lives
 * here too (API keys, devices, connected apps, import, help, sign out…), each
 * as a pushed page rather than a dialog of its own. Only Caliu Pro (the
 * paywall) and small confirmations open on top.
 */
import { LOCALE_META, SUPPORTED_LOCALES, type FontRegion, type Locale } from "@caliu/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { scrollEdgeHairline, useContentScrolledUnder } from "@/hooks/useContentScrolledUnder";
import { Button } from "@/components/ui/Button";
import { Icon, type IconId } from "@/components/ui/Icon";
import { cn } from "@/lib/utils";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSignInGate } from "../../hooks/useSignInGate";
import { useNewsletterToggle } from "../../hooks/useNewsletterToggle";
import { AnalyticsEvent, track } from "../../lib/analytics";
import { prefetchProOffering } from "../../lib/purchases";
import { refreshHelp } from "../../lib/services/helpContentService";
import { getPendingCount } from "../../lib/sync";
import { useAuthStore } from "../../store/auth";
import { useIsPro, useSubscriptionStore } from "../../store/subscription";
import { useUIStore, type SettingsPage } from "../../store/ui";
import { EditNameDialog } from "../EditNameDialog";
import { proStatusLine } from "../../lib/pro";
import { isNewNoteStart, NEW_NOTE_STARTS, type NewNoteStart } from "../../lib/newNoteContent";
import { ProTag } from "../ui/ProTag";
import { SyncIndicator } from "../ui/SyncIndicator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  settingsSurface,
  SettingsCompactProvider,
  SettingsRow,
  SettingsSection,
  SettingsToggleRow,
  useSettingsCompact,
} from "./parts";
import { ApiKeysSettings } from "./ApiKeysSettings";
import { ConflictsSettings } from "./ConflictsSettings";
import { ConnectedAppsSettings } from "./ConnectedAppsSettings";
import { DevicesSettings } from "./DevicesSettings";
import { HelpBreadcrumb, HelpSettings, resolveHelpPage } from "./HelpSettings";
import { getCachedHelp } from "../../lib/services/helpContentService";
import { ImportSettings } from "./ImportSettings";
import { ShortcutsSettings } from "./ShortcutsSettings";
import { ThemeSettings } from "./ThemeSettings";
import { FONT_REGION_TITLE, FontRegionSettings, TypographySettings } from "./TypographySettings";

const FEEDBACK_URL = "mailto:hola@caliuapp.com?subject=Caliu%20feedback";

/** Radix Select needs a non-empty value for "follow the system". */
const SYSTEM_LANGUAGE = "system";

const SORTED_LOCALES = [...SUPPORTED_LOCALES].sort((a, b) =>
  LOCALE_META[a].nativeName.localeCompare(LOCALE_META[b].nativeName),
);

export function SettingsDialog() {
  const { t } = useTranslation();
  const page = useUIStore((s) => s.settingsPage);
  const openSettings = useUIStore((s) => s.openSettings);
  const closeSettings = useUIStore((s) => s.closeSettings);
  const isMobile = useIsMobile();
  const isPro = useIsPro();
  const requireAccount = useSignInGate();

  const [editNameOpen, setEditNameOpen] = useState(false);

  // Warm the paywall offering and the help corpus as Settings opens.
  useEffect(() => {
    if (page !== "root") return;
    if (!isPro) prefetchProOffering();
    void refreshHelp();
  }, [page, isPro]);

  // Import is server-side, so it needs an account (local mode is sent to sign in).
  const openImport = useCallback(() => {
    if (requireAccount("import")) return;
    openSettings("import");
  }, [requireAccount, openSettings]);
  const openImportRef = useRef(openImport);
  openImportRef.current = openImport;
  useEffect(() => {
    const handler = () => openImportRef.current();
    window.addEventListener("caliu:open-import", handler);
    return () => window.removeEventListener("caliu:open-import", handler);
  }, []);

  const go = (next: SettingsPage) => openSettings(next);
  const fontRegion = page?.startsWith("font:") ? (page.slice(5) as FontRegion) : null;
  const helpPage = page === "help" || page?.startsWith("help:") ? (page as "help" | `help:${string}`) : null;
  const back = () => openSettings(parentPage(page ?? "root"));
  const title = (p: SettingsPage): React.ReactNode => {
    if (fontRegion) return t(FONT_REGION_TITLE[fontRegion]);
    if (p.startsWith("help:")) {
      // Phones have no room for the path: just the current page, as on iOS.
      if (isMobile) {
        const { section, article } = resolveHelpPage(p, getCachedHelp());
        return article?.title ?? section?.title ?? t("help.title");
      }
      return <HelpBreadcrumb page={p as `help:${string}`} />;
    }
    return {
      root: t("settings.title"),
      account: t("settings.account"),
      theme: t("userMenu.theme"),
      typography: t("userMenu.typography"),
      editor: t("settings.editor"),
      shortcuts: t("settings.keyboardShortcuts"),
      import: t("importModal.title"),
      conflicts: t("syncIndicator.conflictsTitle"),
      apiKeys: t("userMenu.apiKeys"),
      devices: t("userMenu.devices"),
      connectedApps: t("userMenu.connectedApps"),
      help: t("help.title"),
      general: t("settings.general"),
      notes: t("settings.yourNotes"),
      about: t("settings.about"),
    }[p as Exclude<SettingsPage, `font:${string}` | `help:${string}`>];
  };

  // The row that pushed a page unmounts with it: keep focus inside the panel.
  const backRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (page && page !== "root") backRef.current?.focus();
  }, [page]);

  const actions: RootActions = {
    go,
    openImport,
  };

  return (
    <>
      <Dialog
        open={page !== null}
        onOpenChange={(o) => {
          if (!o) closeSettings();
        }}
      >
        <DialogContent
          // Desktop: no focus ring on the first sidebar item as the window opens.
          onOpenAutoFocus={isMobile ? undefined : (e) => e.preventDefault()}
          // The note list's colour on every size, a step under the cards
          // (bg-sidebar), as the native panes.
          className={cn(
            "flex flex-col overflow-hidden bg-notelist p-0",
            isMobile
              ? "h-full"
              : "h-[min(660px,calc(100dvh-2rem))] max-h-none w-[min(900px,calc(100vw-2rem))] max-w-none flex-row rounded-[22px]",
          )}
        >
          {isMobile ? (
            <>
            {/* Title bar: back (sub-pages), title, Done / X. Drags the phone sheet. */}
              <SettingsTitleBar isMobile={isMobile}>
                {page && page !== "root" && (
                  <Button
                    ref={backRef}
                    variant="outline"
                    size="icon"
                    onClick={back}
                    aria-label={t("common.back")}
                    className={isMobile ? "absolute left-3" : ""}
                  >
                    <Icon name="chevron-left" size={16} />
                  </Button>
                )}
                <DialogTitle
                  className={cn(
                    "min-w-0 font-semibold text-ink",
                    isMobile ? "max-w-[calc(100%-7rem)] truncate text-[16px]" : "flex-1 text-[15px] text-ink-secondary",
                  )}
                >
                  {page ? title(page) : ""}
                </DialogTitle>
                {isMobile ? (
                  <DialogCloseButton size="lg" className="absolute right-3" />
                ) : (
                  page === "root" && (
                    <DialogClose asChild>
                      <Button variant="outline" className="px-4 font-semibold">
                        {t("common.done")}
                      </Button>
                    </DialogClose>
                  )
                )}
              </SettingsTitleBar>
              <SettingsCompactProvider value={isMobile}>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <div
                    className={cn(
                      "mx-auto flex min-h-full w-full max-w-[760px] flex-col",
                      isMobile ? "gap-6 px-4 pb-10 pt-2" : "gap-[18px] px-7 pb-8 pt-2",
                    )}
                  >
                    {page === "root" && <RootPage actions={actions} />}
                    {page === "account" && <AccountPage onEditName={() => setEditNameOpen(true)} />}
                    {page === "theme" && <ThemeSettings />}
                    {page === "typography" && <TypographySettings />}
                    {page === "editor" && <EditorPage />}
                    {fontRegion && <FontRegionSettings region={fontRegion} />}
                    {page === "shortcuts" && <ShortcutsSettings />}
                    {page === "import" && <ImportSettings />}
                    {page === "conflicts" && <ConflictsSettings />}
                    {page === "apiKeys" && <ApiKeysSettings />}
                    {page === "devices" && <DevicesSettings />}
                    {page === "connectedApps" && <ConnectedAppsSettings />}
                    {helpPage && <HelpSettings key={helpPage} page={helpPage} />}
                  </div>
                </div>
              </SettingsCompactProvider>
            </>
          ) : (
            <DesktopSettings
              page={page ?? "root"}
              title={page ? title(page) : ""}
              actions={actions}
              onEditName={() => setEditNameOpen(true)}
            />
          )}
        </DialogContent>
      </Dialog>

      <EditNameDialog open={editNameOpen} onClose={() => setEditNameOpen(false)} />
    </>
  );
}

/* ── Desktop: sidebar by subject + pane (macOS System Settings) ──── */

type Subject = "account" | "general" | "theme" | "typography" | "shortcuts" | "notes" | "help" | "about";

/** The sidebar subject a page belongs to; every page lives under one. */
function subjectOf(page: SettingsPage): Subject {
  if (["root", "account", "apiKeys", "devices", "connectedApps"].includes(page)) return "account";
  if (page === "general" || page === "editor") return "general";
  if (page === "theme") return "theme";
  if (page === "typography" || page.startsWith("font:")) return "typography";
  if (page === "shortcuts") return "shortcuts";
  if (page === "notes" || page === "import" || page === "conflicts") return "notes";
  if (page === "about") return "about";
  return "help";
}

/** The desktop pane's back target; null on a subject's own page. */
function desktopParent(page: SettingsPage): SettingsPage | null {
  if (page.startsWith("font:")) return "typography";
  if (page.startsWith("help:")) return parentPage(page);
  if (page === "apiKeys" || page === "devices" || page === "connectedApps") return "account";
  if (page === "import" || page === "conflicts") return "notes";
  return null;
}

function DesktopSettings({
  page,
  title,
  actions,
  onEditName,
}: {
  page: SettingsPage;
  title: React.ReactNode;
  actions: RootActions;
  onEditName: () => void;
}) {
  const { t } = useTranslation();
  const subject = subjectOf(page);
  const parent = desktopParent(page);
  const fontRegion = page.startsWith("font:") ? (page.slice(5) as FontRegion) : null;
  const helpPage = page === "help" || page.startsWith("help:") ? (page as "help" | `help:${string}`) : null;

  const subjects: { id: Subject; icon: IconId; label: string }[] = [
    { id: "account", icon: "user", label: t("settings.account") },
    { id: "general", icon: "sliders", label: t("settings.general") },
    { id: "theme", icon: "appearance", label: t("userMenu.theme") },
    { id: "typography", icon: "text", label: t("userMenu.typography") },
    { id: "shortcuts", icon: "keyboard", label: t("settings.keyboardShortcuts") },
    { id: "notes", icon: "sync", label: t("settings.yourNotes") },
    { id: "help", icon: "help", label: t("userMenu.help") },
    { id: "about", icon: "info", label: t("settings.about") },
  ];
  const onAboutClick = useAboutClicks();
  const item = (id: Subject, leading: React.ReactNode, label: React.ReactNode) => (
    <Button
      key={id}
      variant="ghost"
      onClick={() => {
        actions.go(id);
        // Hidden: five clicks on About show "What's new" again.
        if (id === "about") onAboutClick();
      }}
      aria-current={subject === id ? "page" : undefined}
      className={cn(
        "h-auto w-full justify-start gap-2.5 rounded-[10px] border-0 px-2.5 py-1.5 text-left font-normal",
        subject === id ? "bg-active hover:bg-active" : "hover:bg-hover",
      )}
    >
      {leading}
      <span className="truncate text-[14px] text-ink">{label}</span>
    </Button>
  );

  return (
    <>
      <nav className="flex w-[220px] shrink-0 flex-col gap-0.5 overflow-y-auto bg-sidebar px-3 pb-3">
        {/* Same height as the pane's header, so the two line up. */}
        <p className="flex h-14 shrink-0 items-center px-2.5 text-[13px] font-semibold text-muted">
          {t("settings.title")}
        </p>
        {subjects.map((s) =>
          item(s.id, <Icon name={s.icon} size={17} className="shrink-0 text-ink" />, s.label),
        )}
      </nav>

      {/* Keyed by page: a new page starts at the top, with no hairline. */}
      <div key={page} className="flex min-w-0 flex-1 flex-col">
        <SettingsTitleBar isMobile={false}>
          {parent && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => actions.go(parent)}
              aria-label={t("common.back")}
            >
              <Icon name="chevron-left" size={16} />
            </Button>
          )}
          <DialogTitle className="min-w-0 flex-1 text-[15px] font-semibold text-ink">
            {parent ? title : subjects.find((s) => s.id === subject)?.label}
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="outline" className="px-4 font-semibold">
              {t("common.done")}
            </Button>
          </DialogClose>
        </SettingsTitleBar>
        <SettingsCompactProvider value={false}>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto flex min-h-full w-full max-w-[640px] flex-col gap-[18px] px-7 pb-8 pt-2">
              {(page === "root" || page === "account") && (
                <>
                  <ProCard />
                  <AccountPage onEditName={onEditName} />
                </>
              )}
              {(page === "general" || page === "editor") && (
                <>
                  <LanguageSection />
                  <EditorPage title={t("settings.editor")} />
                </>
              )}
              {page === "theme" && <ThemeSettings />}
              {page === "typography" && <TypographySettings />}
              {page === "notes" && <NotesSection actions={actions} title={false} />}
              {page === "help" && <HelpSettings page="help" />}
              {page === "about" && <AboutSection actions={actions} title={false} />}
              {fontRegion && <FontRegionSettings region={fontRegion} />}
              {page === "shortcuts" && <ShortcutsSettings />}
              {page === "import" && <ImportSettings />}
              {page === "conflicts" && <ConflictsSettings />}
              {page === "apiKeys" && <ApiKeysSettings />}
              {page === "devices" && <DevicesSettings />}
              {page === "connectedApps" && <ConnectedAppsSettings />}
              {helpPage && helpPage !== "help" && <HelpSettings key={helpPage} page={helpPage} />}
            </div>
          </div>
        </SettingsCompactProvider>
      </div>
    </>
  );
}

/** Where the back button goes: the page that pushed this one. */
function parentPage(page: SettingsPage): SettingsPage {
  if (page.startsWith("font:")) return "typography";
  if (page.startsWith("help:")) {
    const path = page.slice("help:".length);
    return path.includes("/") ? `help:${path.split("/")[0]}` : "help";
  }
  if (page === "apiKeys" || page === "devices" || page === "connectedApps") return "account";
  return "root";
}

function SettingsTitleBar({ isMobile, children }: { isMobile: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const scrolled = useContentScrolledUnder(ref);
  return (
    <div
      ref={ref}
      className={cn(
        scrollEdgeHairline(scrolled),
        "flex shrink-0 items-center gap-3 px-4",
        // Phones: 36px buttons at 12px from the top, as from the sides.
        isMobile ? "relative h-[60px] justify-center" : "h-14",
      )}
    >
      {children}
    </div>
  );
}

/* ── Root ─────────────────────────────────────────────────────────── */

interface RootActions {
  go: (page: SettingsPage) => void;
  openImport: () => void;
}

function RootPage({ actions }: { actions: RootActions }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const localMode = useAuthStore((s) => s.localMode && s.user === null);

  return (
    <>
      <ProCard />

      <SettingsSection title={t("settings.account")}>
        <SettingsRow
          leading={<Avatar name={localMode ? null : (user?.name ?? user?.email ?? null)} />}
          label={
            localMode ? (
              t("settings.noAccount")
            ) : (
              <span className="font-semibold">{user?.name ?? t("userMenu.defaultUserName")}</span>
            )
          }
          subtitle={localMode ? undefined : user?.email}
          onClick={() => actions.go("account")}
        />
      </SettingsSection>

      <SettingsSection title={t("settings.appearance")}>
        <SettingsRow icon="appearance" label={t("userMenu.theme")} onClick={() => actions.go("theme")} />
        <SettingsRow icon="text" label={t("userMenu.typography")} onClick={() => actions.go("typography")} />
      </SettingsSection>

      <SettingsSection title={t("settings.writing")}>
        <SettingsRow icon="edit" label={t("settings.editor")} onClick={() => actions.go("editor")} />
      </SettingsSection>

      <LanguageSection />
      <NotesSection actions={actions} />
      <AboutSection actions={actions} withHelp />
    </>
  );
}

function LanguageSection() {
  const { t, i18n } = useTranslation();
  const language = useUIStore((s) => s.language);
  const setLanguage = useUIStore((s) => s.setLanguage);
  const activeLocale = (i18n.resolvedLanguage || i18n.language) as Locale;
  return (
    <SettingsSection title={t("userMenu.language")}>
      <SettingsRow
        icon="globe"
        label={t("userMenu.language")}
        trailing={
          <Select
            value={language === null ? SYSTEM_LANGUAGE : activeLocale}
            onValueChange={(v) => setLanguage(v === SYSTEM_LANGUAGE ? null : (v as Locale))}
          >
            {/* The native pop-up button: value + up/down chevron on a pill. */}
            <SelectTrigger className="h-8 min-w-0 max-w-[55%] gap-2 whitespace-nowrap bg-active px-3 text-[13px] text-ink hover:bg-hover">
              {/* One line, ellipsized: "Per defecte del sistema" wrapped on phones. */}
              <SelectValue className="min-w-0 truncate" />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value={SYSTEM_LANGUAGE}>{t("settings.systemDefault")}</SelectItem>
              {SORTED_LOCALES.map((l) => (
                <SelectItem key={l} value={l}>
                  {LOCALE_META[l].nativeName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </SettingsSection>
  );
}

function NotesSection({ actions, title = true }: { actions: RootActions; title?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localMode = useAuthStore((s) => s.localMode && s.user === null);
  const closeSettings = useUIStore((s) => s.closeSettings);
  return (
    <SettingsSection title={title ? t("settings.yourNotes") : undefined}>
      {localMode ? (
        <SettingsRow
          icon="sync"
          label={t("settings.notSyncing")}
          subtitle={t("settings.signInToSync")}
          accessory="none"
          onClick={() => {
            closeSettings();
            track(AnalyticsEvent.LocalModeUpgradePrompted, { source: "indicator" });
            navigate("/register");
          }}
        />
      ) : (
        <SettingsRow
          icon="sync"
          label={t("settings.sync")}
          accessory="none"
          trailing={
            <span className="shrink-0 text-[13px] text-muted">
              <SyncIndicator onResolveConflicts={() => actions.go("conflicts")} />
            </span>
          }
        />
      )}
      <SettingsRow icon="download" label={t("commandPalette.importNotes")} onClick={actions.openImport} />
    </SettingsSection>
  );
}

/** Hidden: five clicks on "About" (the section title on phones, the sidebar
 *  item on desktop) show "What's new" again. */
function useAboutClicks() {
  const closeSettings = useUIStore((s) => s.closeSettings);
  const clicks = useRef(0);
  return () => {
    clicks.current += 1;
    if (clicks.current < 5) return;
    clicks.current = 0;
    closeSettings();
    useUIStore.getState().replayWhatsNew();
  };
}

function AboutSection({
  actions,
  withHelp,
  title = true,
}: {
  actions: RootActions;
  withHelp?: boolean;
  /** False on the desktop About page, whose header already says it. */
  title?: boolean;
}) {
  const { t } = useTranslation();
  const closeSettings = useUIStore((s) => s.closeSettings);
  const onAboutTitleClick = useAboutClicks();
  return (
    <SettingsSection title={title ? t("settings.about") : undefined} onTitleClick={onAboutTitleClick}>
      {withHelp && <SettingsRow icon="help" label={t("userMenu.help")} onClick={() => actions.go("help")} />}
      <SettingsRow
        icon="mail"
        label={t("settings.sendFeedback")}
        accessory="external"
        onClick={() => window.open(FEEDBACK_URL, "_self")}
      />
      <SettingsRow
        icon="list-number"
        label={t("userMenu.replayOnboarding")}
        onClick={() => {
          closeSettings();
          useUIStore.getState().openOnboarding({ replay: true });
        }}
      />
    </SettingsSection>
  );
}

/** The Caliu Pro lockup card at the top (native ProStatusCard). */
function ProCard() {
  const { t } = useTranslation();
  const isPro = useIsPro();
  // Before the first answer `isPro` fails open (nothing locked), but the card
  // would then thank a user who may be free: it claims nothing until it knows.
  // With no account there is nothing to wait for: the pitch shows at once.
  const loaded = useSubscriptionStore((s) => s.loaded);
  const hasUser = useAuthStore((s) => s.user !== null);
  const known = loaded || !hasUser;
  const openPaywall = useUIStore((s) => s.openPaywall);
  const openProThanks = useUIStore((s) => s.openProThanks);
  const expiresAt = useSubscriptionStore((s) => s.expiresAt);
  const willRenew = useSubscriptionStore((s) => s.willRenew);
  const plan = useSubscriptionStore((s) => s.plan);
  const source = useSubscriptionStore((s) => s.source);
  const status = isPro ? proStatusLine(t, { plan, expiresAt, willRenew, source }) : null;

  const compact = useSettingsCompact();

  return (
    // Swift `ProStatusCard` in its iOS row: the Settings card's shape and
    // padding, the accent wash for a surface.
    <Button
      variant="ghost"
      disabled={!known}
      onClick={() => (isPro ? openProThanks() : openPaywall("account"))}
      className={cn(
        settingsSurface(compact),
        "pro-upgrade-card h-auto flex-col gap-1.5 border-0 bg-transparent px-4 py-[18px] text-center font-normal hover:bg-hover active:bg-active",
      )}
    >
      <span className="flex items-center gap-2 font-headings text-[22px] font-bold leading-7 text-ink">
        Caliu
        <ProTag size={15} />
      </span>
      {/* Each line sets its own leading: the Button's text-sm 20px squeezed the
          title and spread a wrapped subtitle apart. */}
      {known && (
        <>
          <span className="text-[13px] leading-[18px] text-muted">
            {isPro ? t("pro.thanks") : t("pro.upgradeSubtitle")}
          </span>
          {isPro ? (
            status && <span className="text-[12px] leading-4 text-muted">{status}</span>
          ) : (
            <span className="text-[14px] font-semibold leading-5 text-ink">{t("pro.upgradeNow")}</span>
          )}
        </>
      )}
    </Button>
  );
}

function Avatar({ name, size = 36 }: { name: string | null; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-active font-semibold text-ink-secondary"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
    >
      {name ? name.trim().charAt(0).toUpperCase() : <Icon name="user" size={size * 0.5} />}
    </span>
  );
}

/* ── Account ──────────────────────────────────────────────────────── */

function AccountPage({ onEditName }: { onEditName: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const localMode = useAuthStore((s) => s.localMode && s.user === null);
  const closeSettings = useUIStore((s) => s.closeSettings);
  const openSettings = useUIStore((s) => s.openSettings);
  const isMobile = useSettingsCompact();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [pending, setPending] = useState(0);

  // Signing out keeps the notes on this device (local mode) unless the user
  // asks for the wipe; the confirm dialog is where that choice is made.
  const confirmLogout = async (keepNotes: boolean) => {
    setLogoutOpen(false);
    closeSettings();
    await logout({ keepNotes });
    navigate(keepNotes ? "/app" : "/login");
  };

  return (
    <>
      <div className={cn(settingsSurface(isMobile), "flex items-center gap-4 px-4 py-4")}>
        <Avatar name={localMode ? null : (user?.name ?? user?.email ?? null)} size={52} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[17px] font-semibold text-ink">
            {localMode ? t("settings.noAccount") : (user?.name ?? t("userMenu.defaultUserName"))}
          </span>
          {!localMode && <span className="truncate text-[14px] text-muted">{user?.email}</span>}
        </div>
        {!localMode && (
          <Button variant="ghost" size="icon" onClick={onEditName} aria-label={t("userMenu.editName")}>
            <Icon name="edit" size={16} />
          </Button>
        )}
      </div>

      {localMode ? (
        <SettingsSection title={t("settings.account")} footer={t("settings.notesComeWithYou")}>
          <SettingsRow
            icon="user-plus"
            label={t("settings.signInOrCreate")}
            accessory="none"
            onClick={() => {
              closeSettings();
              navigate("/login");
            }}
          />
        </SettingsSection>
      ) : (
        <>
          {user?.email && <NewsletterSection />}
          <SettingsSection title={t("settings.security")}>
            <SettingsRow icon="key" label={t("userMenu.apiKeys")} onClick={() => openSettings("apiKeys")} />
            <SettingsRow icon="devices" label={t("userMenu.devices")} onClick={() => openSettings("devices")} />
            <SettingsRow icon="plug" label={t("userMenu.connectedApps")} onClick={() => openSettings("connectedApps")} />
          </SettingsSection>
          <SettingsSection>
            <SettingsRow
              icon="sign-out"
              label={t("userMenu.logOut")}
              danger
              accessory="none"
              onClick={async () => {
                setPending(await getPendingCount());
                setLogoutOpen(true);
              }}
            />
          </SettingsSection>
        </>
      )}

      <Dialog alert open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("userMenu.logOutTitle")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 px-4 py-3 text-[13px] leading-relaxed text-ink-secondary">
            <p>{t("userMenu.logOutBody")}</p>
            {pending > 0 && <p>{t("userMenu.logOutPending", { count: pending })}</p>}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t("common.cancel")}
              </Button>
            </DialogClose>
            {/* The wipe is offered, never the default. */}
            <Button variant="danger" size="sm" onClick={() => void confirmLogout(false)}>
              {t("userMenu.logOutAndRemove")}
            </Button>
            <Button variant="primary" size="sm" onClick={() => void confirmLogout(true)}>
              {t("userMenu.logOutKeep")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The account's newsletter toggle (`useNewsletterToggle`, shared with the
 * What's New page). Gone when there is nothing it could do (the address
 * bounced).
 */
function NewsletterSection() {
  const { t } = useTranslation();
  const { newsletter, online, failed, toggle } = useNewsletterToggle();

  if (newsletter && !newsletter.available) return null;

  return (
    <SettingsSection
      title={t("settings.communications")}
      footer={failed ? t("settings.newsletterError") : t("settings.newsletterFooter")}
    >
      <SettingsToggleRow
        icon="mail"
        label={t("settings.newsletter")}
        checked={newsletter?.subscribed ?? false}
        disabled={!newsletter || !online}
        onChange={toggle}
      />
    </SettingsSection>
  );
}

/* ── Editor ───────────────────────────────────────────────────────── */

const NEW_NOTE_START_LABEL: Record<NewNoteStart, string> = {
  h1: "settings.newNoteStartH1",
  h2: "settings.newNoteStartH2",
  h3: "settings.newNoteStartH3",
  h4: "settings.newNoteStartH4",
  date: "settings.newNoteStartDate",
  "date-iso": "settings.newNoteStartDateIso",
  datetime: "settings.newNoteStartDateTime",
  "datetime-iso": "settings.newNoteStartDateTimeIso",
  empty: "settings.newNoteStartEmpty",
};

function EditorPage({ title }: { title?: string }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const maxWidth = useUIStore((s) => s.maxWidth);
  const setMaxWidth = useUIStore((s) => s.setMaxWidth);
  const markdownEnter = useUIStore((s) => s.markdownEnter);
  const setMarkdownEnter = useUIStore((s) => s.setMarkdownEnter);
  const newNoteStart = useUIStore((s) => s.newNoteStart);
  const setNewNoteStart = useUIStore((s) => s.setNewNoteStart);
  const collapsible = useUIStore((s) => s.collapsibleSections);
  const setCollapsible = useUIStore((s) => s.setCollapsibleSections);
  return (
    <SettingsSection title={title}>
      <SettingsRow
        icon="note-new"
        label={t("settings.newNoteStart")}
        subtitle={t("settings.newNoteStartHint")}
        trailing={
          <Select
            value={newNoteStart}
            onValueChange={(v) => {
              if (isNewNoteStart(v)) setNewNoteStart(v);
            }}
          >
            {/* The native pop-up button, as Language uses. */}
            <SelectTrigger
              aria-label={t("settings.newNoteStart")}
              // Its full value, like Swift's fixed-size menu: "Date and time
              // (ISO 8601)" must not read as "Date and time".
              className="h-8 shrink-0 gap-2 whitespace-nowrap bg-active px-3 text-[13px] text-ink hover:bg-hover"
            >
              <SelectValue className="min-w-0 truncate" />
            </SelectTrigger>
            <SelectContent align="end">
              {NEW_NOTE_STARTS.map((mode) => (
                <SelectItem key={mode} value={mode}>
                  {t(NEW_NOTE_START_LABEL[mode])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <SettingsToggleRow
        icon="return"
        label={t("settings.markdownReturn")}
        subtitle={t("settings.markdownReturnHint")}
        checked={markdownEnter}
        onChange={setMarkdownEnter}
      />
      {/* A phone is always narrow: no max width toggle there, like iPhone. */}
      {!isMobile && (
        <SettingsToggleRow
          icon="arrow-left-right"
          label={t("settings.maxWidth")}
          subtitle={t("settings.maxWidthHint")}
          checked={maxWidth}
          onChange={setMaxWidth}
        />
      )}
      <SettingsToggleRow
        icon="chevron-down"
        label={t("settings.collapsibleSections")}
        subtitle={t("settings.collapsibleSectionsHint")}
        badge={t("settings.experimental")}
        checked={collapsible}
        onChange={setCollapsible}
      />
    </SettingsSection>
  );
}
